// S5: pure helpers of the invitations center (Paramètres › Invitations) and of the
// screens that ask for access: a session, a program's sessions, a horse or a team.
// No `@/` import, so `node --test` can load this file directly.
//
// A row comes from GET /user/invitations?box=received|sent:
// { id, kind: 'invite'|'request', model, model_id, status, job, sent_at, responded_at,
//   target: {id, start_at, horse:{id,name}|null} (session) | {id, name, avatar} (horse, team)
//         | {id, name: program title|null, horse:{id,name}|null} (program) | null,
//   from: {id, first_name, last_name, avatar, horse_job?} (received box only),
//   email (sent box, invitations only) }
//
// V2, model 'programs': a vet of the horse asks for the sessions of a program's
// period. Each person who owns some of them receives their own row and answers
// for their own sessions only (no role to choose). The vet's sent box shows the
// request as one line, under the id of its first row, that never names them.
//
// A batch: the invitations one share sent together (a program's « Partager les N
// autres »), one item of the boxes, accepted or refused whole through any of its
// rows (its id). The API groups the rows (Invite::boxLines): the app never does.
// { type: 'batch', count, kind: 'invite', model: 'sessions', model_id: null,
//   target: {horse:{id,name}|null, from, to, program:{id,name}|null,
//            sessions:[{id, start_at, discipline, duration (seconds)|null}]} }

export const BOX_RECEIVED = 'received'
export const BOX_SENT = 'sent'

export const KIND_INVITE = 'invite'
export const KIND_REQUEST = 'request'

export const STATUS_PENDING = 'pending'
export const STATUS_REFUSED = 'refused'
export const STATUSES = [STATUS_PENDING, 'accepted', STATUS_REFUSED, 'cancelled']
// No request waits nor was refused: the access screens (see requestState).
export const STATUS_NONE = 'none'

// The roles an owner can give to someone who asked to join a horse (API UserJob values).
// Choosing one is mandatory to accept such a request.
export const HORSE_ROLES = ['rider', 'coach', 'veterinarian', 'groom', 'physio', 'osteo', 'other']

const MODELS = { sessions: 'session', horses: 'horse', teams: 'team', programs: 'program' }

// The box a `?box=` query opens: the received box unless `sent` is asked for.
export const boxOf = (box) => (box === BOX_SENT ? BOX_SENT : BOX_RECEIVED)

export const invitationsUrl = (box) => `/user/invitations?box=${boxOf(box)}`

// « Tout accepter »: every invitation of the received box, a batch whole, never a
// request (which may need a role). Answers with the received box.
export const ACCEPT_ALL_URL = '/user/invitations/accept-all'
export const ACCEPT_ALL_TOAST_KEY = 'invitations.toast.accepted-all'

export const TYPE_BATCH = 'batch'

export const isPending = (invitation) => invitation?.status === STATUS_PENDING

export const isBatch = (invitation) => invitation?.type === TYPE_BATCH

// The query of a cancel or a resend: a batch (the sent box's line) is withdrawn or
// sent again whole, any other row (a session's « Partage » block) alone.
export const answerParams = (invitation) => (isBatch(invitation) ? { batch: 1 } : {})

// 'invite-session', 'invite-batch', 'invite-horse', 'invite-team', 'request-session',
// 'request-horse', 'request-team', 'request-program', or 'other' for anything this
// version does not know how to word (no one is ever invited to a program: only vets
// ask for its sessions).
export const invitationKind = (invitation) => {
    const model = MODELS[invitation?.model]
    const kind = invitation?.kind
    if (!model || (kind !== KIND_INVITE && kind !== KIND_REQUEST)) return 'other'
    if (model === 'program' && kind !== KIND_REQUEST) return 'other'
    if (isBatch(invitation)) return model === 'session' && kind === KIND_INVITE ? 'invite-batch' : 'other'
    return `${kind}-${model}`
}

// The sessions of a batch, first to last ([] for any other item).
export const batchSessions = (invitation) =>
    isBatch(invitation) && Array.isArray(invitation.target?.sessions) ? invitation.target.sessions : []

// The first and last day of a batch as `format` words a date ({from, to}), a single
// day as {from, to: null}; null for any other item or without a date.
export const batchPeriod = (invitation, format) => {
    if (!isBatch(invitation) || !invitation.target?.from) return null
    const from = format(invitation.target.from)
    const to = invitation.target.to ? format(invitation.target.to) : null
    return { from, to: to && to !== from ? to : null }
}

// How many invitations « Tout accepter » takes (a batch is one): offered from two on.
export const acceptAllCount = (list) =>
    Array.isArray(list) ? list.filter((row) => row?.kind === KIND_INVITE && isPending(row)).length : 0

export const canAcceptAll = (list) => acceptAllCount(list) >= 2

// The page the object opens on. Teams have no page of their own: their settings page.
// A program's sessions open from its horse's page.
export const invitationTarget = (invitation) => {
    const id = invitation?.target?.id
    if (id === undefined || id === null) return null
    switch (invitation.model) {
        case 'sessions':
            return { name: 'sessions.session', params: { sessionId: String(id) } }
        case 'horses':
            return { name: 'horses.horse', params: { horse: String(id) } }
        case 'teams':
            return { name: 'teams.edit', params: { team: String(id) } }
        case 'programs': {
            const horseId = invitation.target.horse?.id
            return horseId === undefined || horseId === null
                ? null
                : { name: 'horses.horse', params: { horse: String(horseId) } }
        }
        default:
            return null
    }
}

// Received: whoever the row is for answers it while it is pending.
export const canAnswer = (invitation, box) => box === BOX_RECEIVED && isPending(invitation)

// Sent: the sender withdraws it while it is pending.
export const canCancel = (invitation, box) => box === BOX_SENT && isPending(invitation)

// Sent and accepted: a link to what is now shared.
export const canOpen = (invitation, box) =>
    box === BOX_SENT && invitation?.status === 'accepted' && invitationTarget(invitation) !== null

// The owner picks the role of whoever asked to join a horse.
export const needsRole = (invitation) => invitation?.kind === KIND_REQUEST && invitation?.model === 'horses'

export const canAccept = (invitation, job) => !needsRole(invitation) || HORSE_ROLES.includes(job)

export const statusKey = (invitation) =>
    `invitations.status.${STATUSES.includes(invitation?.status) ? invitation.status : STATUS_PENDING}`

export const labelKey = (invitation) => `invitations.label.${invitationKind(invitation)}`

export const textKey = (invitation, box) =>
    `invitations.${box === BOX_SENT ? BOX_SENT : BOX_RECEIVED}.${invitationKind(invitation)}`

// answer: 'accepted', 'refused', 'cancelled', or 'resent' (an invitation sent again, from
// the « Partage » block, which has its own text).
export const toastKey = (invitation, answer) =>
    answer === 'resent'
        ? 'sharing.resent'
        : `invitations.toast.${invitation?.kind === KIND_REQUEST ? KIND_REQUEST : KIND_INVITE}-${answer}`

// Same key scheme as the profile's job list: 'horse owner' → user.job.horse-owner.
export const jobKey = (job) => (job ? `user.job.${String(job).toLowerCase().replaceAll(' ', '-')}` : null)

// The role shown on a row: the one an invitation to a horse offers, or the
// requester's role on the horse of the session they ask for. Only a vet of the
// horse may ask for the sessions of a program (the API checks it): told to the
// people asked (received rows carry `from`), not to the vet on her own line.
export const roleOf = (invitation) => {
    if (invitation?.kind === KIND_REQUEST && invitation?.model === 'sessions')
        return invitation?.from?.horse_job ?? null
    if (invitation?.kind === KIND_REQUEST && invitation?.model === 'programs')
        return invitation?.from ? 'veterinarian' : null
    if (invitation?.kind === KIND_INVITE && invitation?.model === 'horses') return invitation?.job ?? null
    return null
}

export const personName = (person) => [person?.first_name, person?.last_name].filter(Boolean).join(' ').trim()

// The red badge: GET /user carries the size of the received box.
export const badgeCount = (user) => {
    const count = Number(user?.pending_invitations_count)
    return Number.isInteger(count) && count > 0 ? count : 0
}

// The API words its 403, 404, 409 and 429 in English only: show our own translated text.
// 409 is the expected one: a co-owner, or the e-mail link, answered first.
const ERROR_KEYS = {
    403: 'invitations.error.forbidden',
    404: 'invitations.error.gone',
    409: 'invitations.error.answered',
    // Sending an invitation again is limited to a few times a minute.
    429: 'invitations.error.too-many',
}
const GENERIC_ERROR_KEY = 'error.there-was-error'
// A 422 carries a validation message the API already translated (null), unless the
// screen words it itself (`unprocessable`).
export const errorKey = (status, unprocessable = null) =>
    ERROR_KEYS[status] ?? (status === 422 ? unprocessable : GENERIC_ERROR_KEY)

// Only GET /user carries the badge count: keep it when another response replaces
// the same user without it (login, profile or avatar update, token refresh).
export const keepBadgeCount = (previous, next) =>
    next &&
    previous &&
    next.pending_invitations_count === undefined &&
    previous.pending_invitations_count !== undefined &&
    String(next.id) === String(previous.id)
        ? { ...next, pending_invitations_count: previous.pending_invitations_count }
        : next

// Asking for access to a session, to a program's sessions (vet) or to a horse or a
// team (join link), then cancelling the request (DELETE invitations/{id}). Each of
// these screens keeps { status: 'none'|'pending'|'refused', requestId, canRequest, … }.

// The reader's latest request as the API sends it ({id, status} or null). Accepted,
// cancelled or unknown reads as none: nothing waits, the reader may ask.
export const requestState = (request) => {
    const status = [STATUS_PENDING, STATUS_REFUSED].includes(request?.status) ? request.status : STATUS_NONE
    return { status, requestId: status === STATUS_NONE ? null : (request.id ?? null) }
}

// Right after asking (201: the request, status 'pending').
export const withPendingRequest = (state, request) =>
    state && request?.id !== undefined && request?.id !== null
        ? { ...state, status: STATUS_PENDING, requestId: request.id, canRequest: false }
        : state

// Right after cancelling: nothing waits any more, the reader may ask again
// (but a member of the horse or team, who has nothing to ask).
export const withoutRequest = (state) =>
    state ? { ...state, status: STATUS_NONE, requestId: null, canRequest: !state.isMember } : state

// Who can cancel: the requester, while the request waits and its id is known.
export const canCancelRequest = (state) => isPending(state) && state.requestId !== null

// A 409 is no error here (a request already waits, e.g. sent from another device,
// or it was answered meanwhile): the screen reads where it stands again. A 422 is
// our generic text unless the screen words it.
export const requestErrorKey = (status, unprocessable = GENERIC_ERROR_KEY) =>
    status === 409 ? null : errorKey(status, unprocessable)
